
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { Loader2, CheckCircle } from "lucide-react";
import { DevotionalFormProps, DevotionalFormValues, ActionStep } from "./types";
import { DevotionalCompletion } from "./DevotionalCompletion";

export function DevotionalForm({
  devotional,
  progress,
  onSubmit,
  isSubmitting
}: DevotionalFormProps) {
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid }
  } = useForm<DevotionalFormValues>({
    defaultValues: {
      completed_prayer: Boolean(progress?.completed_prayer),
      completed_reflection: Boolean(progress?.completed_reflection),
      completed_actions: devotional.action_steps?.map(action => ({
        text: action.text,
        completed: false
      })) || []
    }
  });

  // Set initial values from progress if it exists
  useEffect(() => {
    if (progress) {
      setValue('completed_prayer', Boolean(progress.completed_prayer));
      setValue('completed_reflection', Boolean(progress.completed_reflection));
      if (progress.completed_actions) {
        const completedActions = (progress.completed_actions as ActionStep[]).map((action, index) => ({
          text: devotional.action_steps[index]?.text || '',
          completed: action.completed
        }));
        setValue('completed_actions', completedActions);
      }
    }
  }, [progress, setValue, devotional.action_steps]);

  const watchedPrayer = watch('completed_prayer');
  const watchedReflection = watch('completed_reflection');
  const watchedActions = watch('completed_actions');
  
  const handleActionChange = (index: number, completed: boolean) => {
    const newActions = [...(watchedActions || [])];
    newActions[index] = {
      ...newActions[index],
      completed
    };
    setValue('completed_actions', newActions);
  };

  const allActionsCompleted = watchedActions?.every(action => action.completed) ?? true;
  const isAllCompleted = watchedPrayer && watchedReflection && allActionsCompleted;

  if (progress?.completed_at) {
    return (
      <div className="space-y-6">
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Checkbox id="prayer" checked disabled />
            <label 
              htmlFor="prayer" 
              className="text-sm font-medium leading-none text-muted-foreground"
            >
              I have prayed this prayer
            </label>
          </div>

          <div className="flex items-center space-x-2">
            <Checkbox id="reflection" checked disabled />
            <label 
              htmlFor="reflection" 
              className="text-sm font-medium leading-none text-muted-foreground"
            >
              I have reflected on this verse
            </label>
          </div>

          {devotional.action_steps?.map((action: ActionStep, index: number) => (
            <div key={index} className="flex items-center space-x-2">
              <Checkbox 
                id={`action-${index}`} 
                checked
                disabled
              />
              <label 
                htmlFor={`action-${index}`} 
                className="text-sm font-medium leading-none text-muted-foreground"
              >
                {action.text}
              </label>
            </div>
          ))}
        </div>

        <DevotionalCompletion
          daysCompleted={1}
          points={devotional.points}
          currentDay={devotional.day_number}
        />
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <div className="space-y-4">
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="prayer" 
            checked={watchedPrayer} 
            onCheckedChange={checked => setValue('completed_prayer', checked as boolean)} 
          />
          <label 
            htmlFor="prayer" 
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I have prayed this prayer
          </label>
        </div>

        <div className="flex items-center space-x-2">
          <Checkbox 
            id="reflection" 
            checked={watchedReflection} 
            onCheckedChange={checked => setValue('completed_reflection', checked as boolean)} 
          />
          <label 
            htmlFor="reflection" 
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            I have reflected on this verse
          </label>
        </div>

        {devotional.action_steps?.map((action: ActionStep, index: number) => (
          <div key={index} className="flex items-center space-x-2">
            <Checkbox 
              id={`action-${index}`} 
              checked={watchedActions?.[index]?.completed || false} 
              onCheckedChange={checked => handleActionChange(index, checked as boolean)} 
            />
            <label 
              htmlFor={`action-${index}`} 
              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
            >
              {action.text}
            </label>
          </div>
        ))}
      </div>

      <div className="w-full border-t pt-6">
        <Button 
          type="submit"
          className={`w-full ${
            isAllCompleted ? 'bg-green-500 hover:bg-green-600' : 'bg-blue-600 hover:bg-blue-700'
          } transition-colors duration-200`}
          disabled={!isAllCompleted || isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Completing Devotional...
            </>
          ) : (
            <>
              <CheckCircle className="mr-2 h-4 w-4" />
              {isAllCompleted ? "Complete Devotional" : "Complete All Items"}
            </>
          )}
        </Button>
      </div>
    </form>
  );
}
